/*
 * Superluxe die cut sticker mockup (used by sections/sl-sticker-builder.liquid).
 * Pure client-side canvas, no libraries. Shows the shopper's artwork as a die cut sticker, cut tight to the artwork:
 *  - transparent art: alpha mask -> Euclidean distance transforms -> silhouette of the art (holes, letter counters and
 *    narrow gaps between letters filled, inside corners smoothed by a closing) grown by a tiny offset (~0.5% of the long
 *    side). The offset band is filled by extending the nearest edge pixel color (bleed). Filled gaps/counters take the
 *    edge color when that edge is an outline around a different color (outlined lettering), otherwise they print white
 *    (so plain black text stays legible). Art on top, then a soft drop shadow.
 *  - opaque art (JPG, flat PNG): rounded rectangle sticker with a thin white border + shadow, plus a note.
 * Size/quantity changes only re-draw the finished sticker; the die cut itself is computed once per image.
 */
(function () {
  'use strict';
  var WORK = 720;          // long side (px) the art is processed at; keeps the distance transform well under 100ms
  var OFFSET = 0.005;      // cut line offset beyond the art: 0.5% of the long side (about 4px at 720px), filled with edge color
  var CLOSE = 0.035;       // closing radius: bridges gaps narrower than ~7% of the long side and rounds inside corners
  var SOLID = 100;         // alpha at or above this counts as artwork for the silhouette and edge colors
  var PREVIEWABLE = /^image\/(png|jpe?g|webp|svg\+xml|gif)$/i;
  var INF = 1e20;

  /* ---------- Felzenszwalb & Huttenlocher squared Euclidean distance transform ---------- */
  function edt1d(f, n, d, v, z, near) {
    var k = 0, q, s;
    v[0] = 0; z[0] = -INF; z[1] = INF;
    for (q = 1; q < n; q++) {
      s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
      while (s <= z[k]) {
        k--;
        s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
      }
      k++; v[k] = q; z[k] = s; z[k + 1] = INF;
    }
    k = 0;
    for (q = 0; q < n; q++) {
      while (z[k + 1] < q) k++;
      var dq = q - v[k];
      d[q] = dq * dq + f[v[k]];
      if (near) near[q] = v[k];
    }
  }
  // g: 0 on feature pixels, INF elsewhere -> squared distance to the nearest feature.
  // idx (optional Int32Array): receives the index of that nearest feature pixel (used for edge color bleed).
  function edt(g, w, h, idx) {
    var n = Math.max(w, h), f = new Float64Array(n), d = new Float64Array(n), v = new Int32Array(n), z = new Float64Array(n + 1);
    var near = idx ? new Int32Array(n) : null, iy = idx ? new Int32Array(w * h) : null, x, y;
    for (x = 0; x < w; x++) {
      for (y = 0; y < h; y++) f[y] = g[y * w + x];
      edt1d(f, h, d, v, z, near);
      for (y = 0; y < h; y++) { g[y * w + x] = d[y]; if (iy) iy[y * w + x] = near[y]; }
    }
    for (y = 0; y < h; y++) {
      var o = y * w;
      for (x = 0; x < w; x++) f[x] = g[o + x];
      edt1d(f, w, d, v, z, near);
      for (x = 0; x < w; x++) { g[o + x] = d[x]; if (idx) idx[o + x] = iy[o + near[x]] * w + near[x]; }
    }
    return g;
  }

  function mk(w, h) { var c = document.createElement('canvas'); c.width = Math.max(1, w); c.height = Math.max(1, h); return c; }
  function rrect(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }

  /* ---------- Build the sticker (art + white contour) once per image ---------- */
  function buildSticker(img) {
    var t0 = performance.now();
    var nw = img.naturalWidth || img.width || 1000, nh = img.naturalHeight || img.height || 1000;
    var sc = WORK / Math.max(nw, nh), W = Math.max(1, Math.round(nw * sc)), H = Math.max(1, Math.round(nh * sc));
    var src = mk(W, H), sx = src.getContext('2d', { willReadFrequently: true });
    sx.imageSmoothingQuality = 'high';
    sx.drawImage(img, 0, 0, W, H);
    var px = null;
    try { px = sx.getImageData(0, 0, W, H).data; } catch (e) { px = null; } // tainted (e.g. old Safari + SVG): rectangle fallback
    var shaped = false, bx0 = W, by0 = H, bx1 = -1, by1 = -1;
    if (px) {
      var clear = 0, i, x, y;
      for (y = 0; y < H; y++) for (x = 0; x < W; x++) {
        var a = px[(y * W + x) * 4 + 3];
        if (a < 128) clear++;
        if (a >= SOLID) { if (x < bx0) bx0 = x; if (x > bx1) bx1 = x; if (y < by0) by0 = y; if (y > by1) by1 = y; }
      }
      shaped = bx1 >= 0 && clear > W * H * 0.005;
    }
    var out = shaped ? dieCut(src, px, W, H, bx0, by0, bx1 - bx0 + 1, by1 - by0 + 1) : roundedRect(src, W, H);
    out.ms = Math.round(performance.now() - t0);
    return out;
  }

  function dieCut(src, px, W, H, bx, by, bw, bh) {
    var L = Math.max(bw, bh), o = Math.max(2, Math.round(L * OFFSET)), c = Math.max(4, Math.round(L * CLOSE)), pad = o + c + 3;
    var CW = bw + 2 * pad, CH = bh + 2 * pad, N = CW * CH, g = new Float64Array(N), idx = new Int32Array(N), x, y, i;
    g.fill(INF);
    for (y = 0; y < bh; y++) for (x = 0; x < bw; x++) if (px[((y + by) * W + x + bx) * 4 + 3] >= SOLID) g[(y + pad) * CW + x + pad] = 0;
    edt(g, CW, CH, idx); // distance to the art + nearest art pixel
    // Dilate by o + c, then fill holes and enclosed gaps (flood the outside from the canvas edge)
    var R1 = (o + c) * (o + c), out = new Uint8Array(N), stack = new Int32Array(N), sp = 0;
    function seed(j) { if (!out[j] && g[j] > R1) { out[j] = 1; stack[sp++] = j; } }
    for (x = 0; x < CW; x++) { seed(x); seed(N - CW + x); }
    for (y = 0; y < CH; y++) { seed(y * CW); seed(y * CW + CW - 1); }
    while (sp) {
      var j = stack[--sp], jx = j % CW;
      if (jx > 0) seed(j - 1); if (jx < CW - 1) seed(j + 1);
      if (j >= CW) seed(j - CW); if (j < N - CW) seed(j + CW);
    }
    var dArt = g.slice(); // squared distance to the art, kept for coloring
    // Erode by c (distance to the outside): closing -> cut line sits o beyond the art's silhouette, anti-aliased
    for (i = 0; i < N; i++) g[i] = out[i] ? 0 : INF;
    edt(g, CW, CH);
    var shape = mk(CW, CH), shx = shape.getContext('2d'), id = shx.createImageData(CW, CH), d = id.data;
    // Coloring: the offset band around the art extends the nearest edge color (bleed). Filled gaps and counters take
    // the edge color too when that edge is an outline (e.g. black stroke around white letters), else print white.
    var band = o + 1.5, band2 = band * band, probe = Math.max(6, Math.round(L * 0.06)), kind = new Uint8Array(N);
    function edgeIsOutline(n, p) { // walk from edge pixel n into the art: color change before alpha drops = outline
      if (kind[n]) return kind[n] === 1;
      var nx = n % CW, ny = (n / CW) | 0, sx0 = nx - pad + bx, sy0 = ny - pad + by, ux = 0, uy = 0, res = 2, a, b;
      // Inward direction at the edge = toward the alpha mass in a 7x7 window (stable at corners), else away from p
      for (b = -3; b <= 3; b++) for (a = -3; a <= 3; a++) {
        var wx = sx0 + a, wy = sy0 + b;
        if (wx >= 0 && wy >= 0 && wx < W && wy < H) { var al = px[(wy * W + wx) * 4 + 3]; ux += a * al; uy += b * al; }
      }
      if (!ux && !uy) { ux = nx - p % CW; uy = ny - ((p / CW) | 0); }
      var ul = Math.sqrt(ux * ux + uy * uy) || 1; ux /= ul; uy /= ul;
      var q0 = (sy0 * W + sx0) * 4, r0 = px[q0], g0 = px[q0 + 1], b0 = px[q0 + 2];
      for (var t = 1; t <= probe; t++) {
        var qx = Math.round(nx + ux * t) - pad + bx, qy = Math.round(ny + uy * t) - pad + by;
        if (qx < 0 || qy < 0 || qx >= W || qy >= H) break;
        var q = (qy * W + qx) * 4;
        if (px[q + 3] < SOLID) break;
        if (Math.abs(px[q] - r0) + Math.abs(px[q + 1] - g0) + Math.abs(px[q + 2] - b0) > 120) { res = 1; break; }
      }
      kind[n] = res; return res === 1;
    }
    var mx0 = CW, my0 = CH, mx1 = 0, my1 = 0;
    for (y = 0; y < CH; y++) for (x = 0; x < CW; x++) {
      i = y * CW + x;
      var e = Math.sqrt(g[i]) - c; // distance inside the cut line
      if (e <= 0) continue;
      var k = i * 4, n = idx[i];
      if (dArt[i] <= band2 || edgeIsOutline(n, i)) {
        var q = (((n / CW) | 0) - pad + by) * W * 4 + (n % CW - pad + bx) * 4;
        d[k] = px[q]; d[k + 1] = px[q + 1]; d[k + 2] = px[q + 2];
      } else { d[k] = d[k + 1] = d[k + 2] = 255; }
      d[k + 3] = e >= 1 ? 255 : Math.round(e * 255);
      if (x < mx0) mx0 = x; if (x > mx1) mx1 = x; if (y < my0) my0 = y; if (y > my1) my1 = y;
    }
    shx.putImageData(id, 0, 0);
    shx.drawImage(src, bx, by, bw, bh, pad, pad, bw, bh);
    var sw = mx1 - mx0 + 1, sh = my1 - my0 + 1, st = mk(sw, sh);
    st.getContext('2d').drawImage(shape, mx0, my0, sw, sh, 0, 0, sw, sh);
    return { canvas: st, shaped: true, border: o };
  }

  function roundedRect(src, W, H) {
    var r = Math.max(3, Math.round(Math.max(W, H) * 0.012)), ro = Math.round(Math.max(W, H) * 0.035), ri = Math.max(2, ro - r); // thin white border
    var st = mk(W + 2 * r, H + 2 * r), x = st.getContext('2d');
    x.fillStyle = '#fff'; rrect(x, 0, 0, st.width, st.height, ro); x.fill();
    x.save(); rrect(x, r, r, W, H, ri); x.clip(); x.drawImage(src, r, r); x.restore();
    return { canvas: st, shaped: false, border: r };
  }

  /* ---------- Wire up each sticker builder ---------- */
  function init(root) {
    if (root.__slmock) return; root.__slmock = true;
    var form = root.querySelector('[data-sl-sb-form]');
    var panel = root.querySelector('[data-sl-sb-mock]');
    if (!form || !panel || !window.HTMLCanvasElement) return;
    var cv = panel.querySelector('[data-sl-sb-mock-canvas]'), stage = panel.querySelector('.sl-sb__mock-stage');
    var labelEl = panel.querySelector('[data-sl-sb-mock-label]'), noteEl = panel.querySelector('[data-sl-sb-mock-note]');
    var msgEl = root.querySelector('[data-sl-sb-mock-msg]');
    var fileIn = root.querySelector('[data-sl-sb-file]');
    var rep = panel.querySelector('[data-sl-sb-mock-replace]');
    var nf = new Intl.NumberFormat('en-US');
    var sticker = null, token = 0, objUrl = null;
    var item = { one: 'sticker', many: 'stickers' };
    try { var cfg = JSON.parse(root.querySelector('[data-sl-sb-data]').textContent); if (cfg.item) item = cfg.item; } catch (e) {}

    function checked(n) { var c = form.querySelector('input[name="' + n + '"]:checked'); return c ? c.value : null; }
    function num(n) { var el = form.querySelector('[name="' + n + '"]'); var v = el && !el.disabled ? parseFloat(el.value) : NaN; return v > 0 ? v : null; }
    function fmtIn(v) { return (Math.round(v * 100) / 100) + ' in'; }
    function dims() { // chosen width/height in inches (null when unknown)
      var s = checked('size');
      if (s && s !== 'custom') { var m = s.split('x'); return { w: +m[0], h: +m[1] }; }
      if (s === 'custom') {
        var w = num('w'), h = num('h'), ar = sticker ? sticker.canvas.width / sticker.canvas.height : 1;
        if (w && h) return { w: w, h: h };
        if (w) return { w: w, h: w / ar };
        if (h) return { w: h * ar, h: h };
      }
      return null;
    }
    function label() {
      var s = checked('size'), q = checked('qty'), parts = [];
      if (s === 'custom') { var w = num('w'), h = num('h'); parts.push(w && h ? fmtIn(w) + ' x ' + fmtIn(h) : 'Custom size'); }
      else if (s) { var m = s.split('x'); parts.push(m[0] + ' in x ' + m[1] + ' in'); }
      if (q === 'custom') { var cq = num('cq'); parts.push(cq ? nf.format(Math.round(cq)) + ' ' + item.many : 'Custom quantity'); }
      else if (q) parts.push(nf.format(+q) + ' ' + item.many);
      return parts.join(', ');
    }

    function draw() {
      if (!sticker) return;
      var cssW = stage.clientWidth; if (!cssW) return;
      var d = dims(), sw = sticker.canvas.width, sh = sticker.canvas.height;
      var boxAR = d ? d.w / d.h : sw / sh;
      var cssH = Math.round(Math.max(190, Math.min(300, cssW / Math.max(1, Math.min(boxAR, 1.9)))));
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.style.height = cssH + 'px';
      cv.width = Math.round(cssW * dpr); cv.height = Math.round(cssH * dpr);
      var ctx = cv.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);
      // Fit the chosen width x height box into the stage, then the sticker proportionally inside that box
      var pad = Math.round(Math.min(cssW, cssH) * 0.13), aw = cssW - 2 * pad, ah = cssH - 2 * pad;
      var bw = aw, bh = aw / boxAR; if (bh > ah) { bh = ah; bw = ah * boxAR; }
      var k = Math.min(bw / sw, bh / sh), dw = sw * k, dh = sh * k, x = (cssW - dw) / 2, y = (cssH - dh) / 2 - dh * 0.02;
      ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
      var m = Math.max(dw, dh);
      ctx.save(); // soft drop shadow
      ctx.shadowColor = 'rgba(18,18,18,.26)'; ctx.shadowBlur = m * 0.07; ctx.shadowOffsetY = m * 0.035;
      ctx.drawImage(sticker.canvas, x, y, dw, dh);
      ctx.shadowColor = 'rgba(18,18,18,.16)'; ctx.shadowBlur = Math.max(1.5, m * 0.008); ctx.shadowOffsetY = Math.max(0.5, m * 0.003);
      ctx.drawImage(sticker.canvas, x, y, dw, dh);
      ctx.restore();
      labelEl.textContent = label();
      cv.setAttribute('aria-label', 'Preview of your artwork as a die cut sticker' + (labelEl.textContent ? ', ' + labelEl.textContent : ''));
    }
    function show(st) {
      sticker = st;
      var was = panel.hidden;
      panel.hidden = !st;
      if (st) { noteEl.hidden = st.shaped; panel.setAttribute('data-ms', st.ms); draw(); }
      if (was !== panel.hidden) window.dispatchEvent(new Event('resize')); // lets the card re-check whether it can stay sticky
    }
    function msg(t) { if (!msgEl) return; msgEl.textContent = t || ''; msgEl.hidden = !t; }

    function load(url, cb) {
      var img = new Image(), done = false;
      img.decoding = 'async';
      var to = setTimeout(function () { if (!done) { done = true; cb(null); } }, 10000);
      img.onload = function () { if (done) return; done = true; clearTimeout(to); cb(img); };
      img.onerror = function () { if (done) return; done = true; clearTimeout(to); cb(null); };
      img.src = url;
    }
    function build(img) {
      try { return buildSticker(img); } catch (e) { return null; }
    }

    function onFile() {
      var f = fileIn && fileIn.files && fileIn.files[0], my = ++token;
      if (objUrl) { URL.revokeObjectURL(objUrl); objUrl = null; }
      msg('');
      if (!f) { show(null); return; }
      var isImg = PREVIEWABLE.test(f.type || '') || /\.(png|jpe?g|webp|svg|gif)$/i.test(f.name || '');
      if (!isImg) { show(null); msg('A preview is shown for PNG, JPG, WEBP and SVG files. We\u2019ll review your file before printing.'); return; }
      objUrl = URL.createObjectURL(f);
      load(objUrl, function (img) {
        if (my !== token) return;
        var st = img ? build(img) : null;
        if (!st) msg('We couldn\u2019t preview this file.');
        show(st);
      });
    }
    if (fileIn) fileIn.addEventListener('change', onFile);
    form.addEventListener('change', function (e) { if (/^(size|qty|w|h|cq)$/.test(e.target.name || '')) draw(); });
    form.addEventListener('input', function (e) { if (/^(w|h|cq)$/.test(e.target.name || '')) draw(); });
    if (rep) rep.addEventListener('click', function () { if (fileIn) fileIn.click(); });
    var rt; window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(draw, 120); }, { passive: true });
    // Back/forward cache or a browser-restored file selection
    if (fileIn && fileIn.files && fileIn.files.length) onFile();
  }

  function boot() { Array.prototype.forEach.call(document.querySelectorAll('[data-sl-sb]'), init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
